<template>
  <b-form-group
    class="phone-field m-0"
    :class="{'phone-field-error': isRequired || (phoneObj.phone !== '' && !isValid) || isErrorPhoneNumber || isMinLengthError}"
  >
    <div class="d-flex justify-content-between">
      <label class="form-label">
        {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.labelPhoneNumber') }} <span class="text-danger">*</span>
      </label>
    </div>
    <int-tel-input
      v-if="phoneObj.countryAbbr"
      id="verify-phone-number"
      data-testid="verify-phone-number"
      :phone="phoneObj.phone"
      :country-abbr="phoneObj.countryAbbr"
      :placeholder="$t('stepGuideControl.placeholderPhoneNumber')"
      :class="{ 'phone-field--country-disabled': disableCountrySelect }"
      @country-change="countryCodeChange"
      @phone-change="phoneNumberChange"
    />
    <small
      v-if="isRequired || isErrorPhoneNumber"
      data-testid="error-required-phone-number"
      class="text-danger"
    >
      {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.errorPhoneNumberRequired') }}
    </small>
    <small
      v-else-if="isMinLengthError"
      data-testid="error-min-length-phone-number"
      class="text-danger"
    >
      {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.errorPhoneNumberMinLength', { min: minLength }) }}
    </small>
    <small
      v-else-if="phoneObj.phone !== '' && !isValid"
      data-testid="error-invalid-phone-number"
      class="text-danger"
    >
      {{ $t('stepGuideControl.stepVerifyInfo.formPrimaryContact.errorPhoneNumberInvalid') }}
    </small>
  </b-form-group>
</template>

<script>
import { BFormGroup } from 'bootstrap-vue'
import IntTelInput from 'int-tel-input'
import { isValidPhoneNumber } from 'libphonenumber-js'

export default {
  components: {
    BFormGroup,
    IntTelInput,
  },

  props: {
    phoneData: {
      type: Object,
      required: true,
      default: () => {
      },
    },

    isErrorPhoneNumber: {
      type: Boolean,
      required: false,
      default: false,
    },

    disableCountrySelect: {
      type: Boolean,
      default: false,
    },

    minLength: {
      type: Number,
      default: 3,
    },
  },

  data() {
    return {
      phoneObj: {
        countryAbbr: '',
        countryCode: '',
        phone: '',
        isValid: true,
        isRequired: false,
      },
      isTooShort: false,
    }
  },

  computed: {
    isRequired() {
      return this.phoneObj.isRequired
    },
    isValid() {
      return this.phoneObj.isValid
    },
    isMinLengthError() {
      return this.isTooShort && this.phoneObj.phone !== '' && !this.isRequired
    },
  },

  watch: {
    phoneData: {
      handler(val) {
        this.phoneObj = val
        if (!this.phoneObj.countryAbbr) {
          this.getCountryCode()
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    async getCountryCode() {
      try {
        const response = await fetch('https://api.country.is/')
        const data = await response.json()
        this.phoneObj.countryAbbr = data.country?.toLowerCase() || 'vn'
      } catch (e) {
        // Fallback: default 'vn' countryAbbr
        this.phoneObj.countryAbbr = 'vn'
      }
    },

    countryCodeChange(country) {
      this.phoneObj.countryAbbr = country.addr
      this.phoneObj.countryCode = country.code
      // Re-validate current phone number with the new country code
      if (this.phoneObj.phone) {
        this.checkValid(this.phoneObj.phone)
      } else {
        this.$emit('update:phone-data', this.phoneObj)
      }
    },
    phoneNumberChange(phone) {
      this.checkValid(phone)
    },
    checkValid(phone) {
      this.phoneObj.phone = phone
      if (phone) {
        this.phoneObj.isRequired = false

        // Check min length before attempting to parse
        if (phone.length < this.minLength) {
          this.isTooShort = true
          this.phoneObj.isValid = false
          this.$emit('update:phone-data', this.phoneObj)
          return
        }

        this.isTooShort = false

        try {
          // Validate using country code from selected flag (countryAbbr is lowercase)
          const countryCode = (this.phoneObj.countryAbbr || 'VN').toUpperCase()
          this.phoneObj.isValid = isValidPhoneNumber(phone, countryCode)
        } catch (e) {
          this.phoneObj.isValid = false
        }

        this.$emit('update:phone-data', this.phoneObj)
      } else {
        this.phoneObj.isRequired = true
        this.isTooShort = false
      }
    },
  },
}
</script>

<style lang="scss">
  .phone-field {

    .int-tel-input-wapper .int-tel-input input, .int-tel-input-wapper .int-tel-select > input {
      height: 48px !important;
      border: 1px solid #e0e0e5;

      &::-webkit-input-placeholder {
        font-size: 16px;
        color: #B9B9C3;
      }
    }

    .int-tel-input-wapper .int-tel-select > input {
      /* border-bottom-left-radius: .625rem;
      border-top-left-radius: .625rem; */
      border-bottom-left-radius: var(--input-border-radius-base);
      border-top-left-radius: var(--input-border-radius-base);
    }

    .int-tel-input-wapper .int-tel-input input {
      border-left: 0;
      /* border-bottom-right-radius: .625rem;
      border-top-right-radius: .625rem; */
      border-bottom-right-radius: var(--input-border-radius-base);
      border-top-right-radius: var(--input-border-radius-base);
    }

    .int-tel-input-wapper .select-dropdown .scrollbar__wrap .scrollbar li.options .int-countries-flag {
      display: none !important;
    }

    .int-tel-input-wapper .int-tel-input input:hover, .int-tel-input-wapper .int-tel-select > input:hover{
      border-color: #e0e0e5;
      box-shadow: none;
    }

    .int-tel-input-wapper .int-tel-input input:focus {
      border-color: #16213E;
      box-shadow: none;
      border-left: 1px solid #16213E;
    }

    .int-tel-input-wapper .int-tel-select > input:focus {
      border-color: #16213E;
      box-shadow: none;
    }

    .int-tel-input-wapper .select-dropdown .scrollbar__wrap .scrollbar li.options{
      color: #16213E !important;
      padding: 10px;
      font-size: 14px;
    }

    .int-tel-input-wapper .select-dropdown .scrollbar__wrap .scrollbar li.options:hover{
      background-color: #fcf7f8;
    }

    .int-tel-input-wapper .select-dropdown .scrollbar__wrap .scrollbar li.options.selected {
      background-color: #fcf7f8;
    }

    .filter-alphabet {
      display: none;
    }
  }

  .phone-field-error{
    .int-tel-input-wapper .int-tel-input input {
      border: 1px solid #E11C4E !important;
    }
  }

  .phone-field--country-disabled.int-tel-input-wapper {
    .int-tel-select {
      pointer-events: none !important;
      cursor: not-allowed;
      opacity: 0.7;
      background-color: #f5f5f5;
    }
  }
</style>
